import {Injectable} from '@angular/core';
import {EventOwner, User, UserBasicPut, UserCore, UserDetail, UserPost, UserSalespoint, UsersService as Api, UserStatus} from '../../generated/sales-terminal-client';
import {SnackService} from '../snack/snack.service';
import {Observable, tap} from 'rxjs';
import {PageableResult, ScrollLoadService, SortableSearch} from '../../components/scroll-load-component/scroll-load.component';
import {ScrollLoad} from '../../components/scroll-load-component/ScrollLoad';
import {cloneDeep} from 'lodash-es';
import {UsersFilter} from '../../modules/admin/users/list/users-list.component';
import {ValidationService} from '../validation/validation.service';
import {Privileges} from "../../modules/admin/privileges/privileges.constants";

export interface UserGet extends SortableSearch {
  filters?: UsersFilter
}

@Injectable()
export class UserService implements ScrollLoadService<User> {
  constructor(private _api: Api,
              private snackService: SnackService) {}

  static getData(user: User): UserPost {
    const data: User = cloneDeep(user);
    data.firstName = data.firstName.trim();
    data.lastName = data.lastName.trim();
    delete data.userId;
    delete data.userStatus;
    data.legalPersons = data.legalPersons.map((lp: UserDetail) => {
      delete lp.companyName;
      lp.salespoints.forEach((sp: UserSalespoint) => {
        if (sp.alias && sp.alias.id) {
          lp.salespoints.push(sp.alias);
        }
      });
      lp.salespoints = lp.salespoints.map((sp: UserSalespoint) => {
        delete sp.name;
        delete sp.ownerCentreId;
        delete sp.roleNames;
        delete sp.alias;
        return sp;
      });
      return lp;
    });
    return data as UserPost;
  }

  static getBasicData(user: User): UserBasicPut {
    const data: any = <UserBasicPut>cloneDeep(user);
    delete data.userId;
    delete data.userStatus;
    delete data.legalPersons;
    delete data.pendingEmail;
    delete data.defaultLang;
    return data;
  }

  public isValid(user: User): boolean {
    if (!user) {
      return false;
    }
    const name = user.firstName && ValidationService.validateString(user.firstName, 2) &&
      user.lastName && ValidationService.validateString(user.lastName, 2);
    const email = user.email && ValidationService.validateEmail(user.email);
    const phone = !user.phoneNr || ValidationService.validatePhone(user.phoneNr);
    const legalPersons = user.legalPersons?.length && !user.legalPersons.filter((lp: UserDetail) => {
      return !lp.legalPersonId || !lp.salespoints || lp.salespoints.filter((sp: UserSalespoint) => !sp.id || !sp.roleIds
        || !sp.roleIds.length || (sp.alias && sp.alias.id && (!sp.alias.roleIds || !sp.alias.roleIds.length))).length;
    }).length;
    return !!(name && email && phone && legalPersons);
  }

  public search(data: UserGet): Observable<PageableResult<User>> {
    return this._api.usersGet(
      data.filters?.name,
      data.filters?.email,
      data.filters?.legalPersonId,
      data.filters?.salespointId,
      data.filters?.status,
      data.filters?.privilege,
      data.page,
      data.pageSize,
      data.sortOrder
    ).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public displayScrollLoadOption(option: User): string {
    return option.firstName + ' ' + option.lastName;
  }

  public displayScrollLoadSelection(selection: User): string | undefined {
    if (!selection) { return ''; }
    return selection.firstName + ' ' + selection.lastName;
  }

  public getScrollLoad(pageSize?: number, idKey?: string): ScrollLoad<User> {
    return new ScrollLoad<User>(pageSize || 50, idKey || 'userId');
  }

  public createUser(data: User): Observable<User> {
    return this._api.userPost(data).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public getUser(id: string): Observable<User> {
    return this._api.usersGetById(id).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public updateUser(id: string, data: User): Observable<User> {
    return this._api.usersPutById(id, data).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public activateUser(id: string): Observable<any> {
    return this._api.activateUserById(id).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public deactivateUser(id: string): Observable<any> {
    return this._api.deactivateUserById(id).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public archiveUser(id: string): Observable<any> {
    return this._api.archiveUserById(id).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public getActiveUserBySalespoint(salespointId: number): Observable<UserCore[]> {
    return this._api.usersSalespointIdGet(salespointId).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public resendEmailConfirmation(id: string): Observable<any> {
    return this._api.resendNewEmailConfirmation(id).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public clearSessions(id: string): Observable<any> {
    return this._api.userUserIdClearSessionsPost(id).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public findLegalEventOwnersForUser(): Observable<EventOwner[]> {
    return this._api.userEventownersGet().pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) }))
  }

  public setPageableQuery(query: string): UserGet {
    return { filters: { name: query } };
  }

  public setProjectManagerQuery(query: string): UserGet {
    return { filters: { name: query, privilege: Privileges.LEGAL_PERSON_PROJECT_MANAGER, status: UserStatus.Active } };
  }
}
